// Detects absolute file-path references inside a rep's plain-text chat message so the UI can
// splice in an inline "Download" button next to each one (see ChatDock.tsx's MessageText).
//
// Deliberately scoped to the corpus root so we don't flag every stray "/" in prose as a path.
export const FILE_PATH_PREFIX = '/home/joshua/hq/stories/spiritual_data';

// Characters that can appear inside a path token once we're past the prefix.
const PATH_CHAR_RE = /[A-Za-z0-9_\-./]/;

// Trailing characters that are almost always sentence punctuation, not part of the path, when
// they immediately follow a path token (e.g. "...main.py." or "...file.ts, and also...").
const TRAILING_PUNCT_RE = /[.,);:'"]+$/;

export interface FilePathMatch {
  path: string;
  start: number;
  end: number;
}

// Scans `text` for absolute paths under FILE_PATH_PREFIX and returns each match with its
// start/end offset in the ORIGINAL string, so a caller can splice text segments around it
// without disturbing the rest of the message. Bare directory paths (no '.' in the final
// segment) are skipped to cut down on noise, since most useful links are to a specific file.
export function detectFilePaths(text: string): FilePathMatch[] {
  if (!text) return [];
  const matches: FilePathMatch[] = [];
  let searchFrom = 0;

  while (true) {
    const idx = text.indexOf(FILE_PATH_PREFIX, searchFrom);
    if (idx === -1) break;

    // Require a token boundary right before the prefix, so we don't match a prefix that's
    // embedded inside some longer non-path token.
    const before = idx > 0 ? text[idx - 1] : '';
    if (before && PATH_CHAR_RE.test(before)) {
      searchFrom = idx + FILE_PATH_PREFIX.length;
      continue;
    }

    // Extend through path-safe characters.
    let end = idx + FILE_PATH_PREFIX.length;
    while (end < text.length && PATH_CHAR_RE.test(text[end])) {
      end++;
    }

    const raw = text.slice(idx, end);
    const trimmed = raw.replace(TRAILING_PUNCT_RE, '');

    if (trimmed.length > FILE_PATH_PREFIX.length) {
      const lastSlash = trimmed.lastIndexOf('/');
      const finalSegment = trimmed.slice(lastSlash + 1);
      // Prefer paths that look like an actual file (an extension in the last segment) over
      // bare directory paths.
      if (finalSegment.includes('.')) {
        matches.push({ path: trimmed, start: idx, end: idx + trimmed.length });
      }
    }

    searchFrom = end;
  }

  return matches;
}
